import React, { useEffect, Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import { Toaster } from './components/ui/toaster';

// Keep the landing page eager so the marketing hero paints instantly.
// Every other page is code-split — a first-time visitor to /pricing no longer
// downloads the entire authenticated dashboard bundle.
import Landing from './pages/Landing';
import AuthCallback from './pages/AuthCallback';
import WhatsAppSupport from './components/WhatsAppSupport';

/**
 * retryLazy: wraps React.lazy so a stale-chunk failure (common after a fresh
 * deploy while the user has an older bundle open) recovers automatically.
 *
 * Flow: try the dynamic import → if it rejects with a ChunkLoadError-shaped
 * error, wait 400ms and try once more (chunks might still be uploading).
 * If it fails a second time we hard-reload the page with a `?_cb=` bust so
 * webpack fetches a fresh manifest and hashed chunks. A sessionStorage guard
 * makes sure we don't get stuck in a reload loop if the chunk really is
 * missing (bad build).
 */
function retryLazy(factory) {
  const RELOAD_GUARD = '__cj_chunk_reload_at';
  const isChunkError = (err) => {
    if (!err) return false;
    const name = err.name || '';
    const msg = err.message || '';
    return name === 'ChunkLoadError'
      || /Loading chunk \S+ failed/i.test(msg)
      || /Unexpected token '<'/i.test(msg); // ingress returned HTML for a missing .js
  };
  return lazy(() => factory().catch((err) => {
    if (!isChunkError(err)) throw err;
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        factory().then(resolve).catch((err2) => {
          if (!isChunkError(err2)) return reject(err2);
          try {
            const now = Date.now();
            const last = Number(sessionStorage.getItem(RELOAD_GUARD) || 0);
            // If we already reloaded within the last 20s, give up and let
            // the error bubble — a real bad build shouldn't loop forever.
            if (now - last < 20000) return reject(err2);
            sessionStorage.setItem(RELOAD_GUARD, String(now));
          } catch { /* sessionStorage disabled — still try reload */ }
          window.location.reload();
        });
      }, 400);
    });
  }));
}

const HowItWorks     = retryLazy(() => import('./pages/HowItWorks'));
const Features       = retryLazy(() => import('./pages/Features'));
const Pricing        = retryLazy(() => import('./pages/Pricing'));
const About          = retryLazy(() => import('./pages/About'));
const Terms          = retryLazy(() => import('./pages/Terms'));
const Contact        = retryLazy(() => import('./pages/Contact'));
const FAQ            = retryLazy(() => import('./pages/FAQ'));
const Login          = retryLazy(() => import('./pages/Login'));
const GetStarted     = retryLazy(() => import('./pages/GetStarted'));
const Dashboard      = retryLazy(() => import('./pages/Dashboard'));
const RecipesPage    = retryLazy(() => import('./pages/app/RecipesPage'));
const StoriesPage    = retryLazy(() => import('./pages/app/StoriesPage'));
const VoiceRecipesPage = retryLazy(() => import('./pages/app/VoiceRecipesPage'));
const AlbumsPage     = retryLazy(() => import('./pages/app/AlbumsPage'));
const FamilyTreePage = retryLazy(() => import('./pages/app/FamilyTreePage'));
const SearchPage     = retryLazy(() => import('./pages/app/SearchPage'));
const SettingsPage   = retryLazy(() => import('./pages/app/SettingsPage'));
const NotificationsPage = retryLazy(() => import('./pages/app/NotificationsPage'));
const PublicCookbook = retryLazy(() => import('./pages/PublicCookbook'));
const PublicRecipe   = retryLazy(() => import('./pages/PublicRecipe'));
const PublicStory    = retryLazy(() => import('./pages/PublicStory'));
const JoinFamily     = retryLazy(() => import('./pages/JoinFamily'));
const RecordRecipeRequest = retryLazy(() => import('./pages/RecordRecipeRequest'));
const MarketingInApp = retryLazy(() => import('./pages/app/MarketingInApp'));
const AdminPage      = retryLazy(() => import('./pages/app/AdminPage'));

function RouteFallback() {
  // Warm cream backdrop matching the app so lazy chunks never flash white.
  return (
    <div className="min-h-screen bg-cream flex items-center justify-center">
      <div className="w-10 h-10 rounded-full border-2 border-cumin-green border-t-transparent animate-spin" />
    </div>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRoutes() {
  const location = useLocation();

  // Fire a Google Analytics page_view on every SPA route change. gtag's
  // auto-tracking only sees the initial page load; React Router transitions
  // are invisible to it, so we emit manually. Guarded so it's a no-op if
  // gtag failed to load (adblockers, offline, etc.).
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    const page_path = location.pathname + location.search;
    window.gtag('event', 'page_view', {
      page_path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);

  // Detect Emergent OAuth callback SYNCHRONOUSLY during render — before routes run
  if ((location.hash || '').includes('session_id=')) {
    return <AuthCallback />;
  }
  // Hide the floating support button on the stripped-down guest flows
  // (record + join) where every distraction hurts conversion.
  const hideSupport = /^\/(record|join)\//.test(location.pathname);
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/features" element={<Features />} />
        <Route path="/stories" element={<Navigate to="/" replace />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/about" element={<About />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Terms />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/login" element={<Login />} />
        <Route path="/get-started" element={<GetStarted />} />
        <Route path="/app" element={<Dashboard />} />
        <Route path="/app/recipes" element={<RecipesPage />} />
        <Route path="/app/stories" element={<StoriesPage />} />
        <Route path="/app/voice-recipes" element={<VoiceRecipesPage />} />
        <Route path="/app/albums" element={<AlbumsPage />} />
        <Route path="/app/family-tree" element={<FamilyTreePage />} />
        <Route path="/app/search" element={<SearchPage />} />
        <Route path="/app/settings" element={<SettingsPage />} />
        <Route path="/app/notifications" element={<NotificationsPage />} />
        <Route path="/app/admin" element={<AdminPage />} />
        <Route path="/app/how-it-works" element={<MarketingInApp />} />
        <Route path="/app/features" element={<MarketingInApp />} />
        <Route path="/app/pricing" element={<MarketingInApp />} />
        <Route path="/app/about" element={<MarketingInApp />} />
        <Route path="/cookbook/:token" element={<PublicCookbook />} />
        <Route path="/r/:token" element={<PublicRecipe />} />
        <Route path="/s/:token" element={<PublicStory />} />
        <Route path="/join/:token" element={<JoinFamily />} />
        <Route path="/record/:token" element={<RecordRecipeRequest />} />
      </Routes>
      {!hideSupport && <WhatsAppSupport />}
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AppRoutes />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
